export type Volume = {
  number: string;
  title: string;
  subtitle: string;
  intro: string[];
  inside: string[];
  result: string;
  closing: string;
  safety?: string;
};

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'HVAC Electrical Fault Assessment Blueprint™',
    subtitle: 'Turn Equipment Symptoms Into a Structured Investigation Plan',
    intro: [
      'Every effective troubleshooting process begins with a clear description of the problem.',
      'This volume provides a practical framework for organizing reported symptoms, equipment observations, service history, and initial diagnostic priorities.',
    ],
    inside: [
      'HVAC Electrical Symptom Intake Checklist',
      'Equipment Operating Condition Worksheet',
      'Electrical Fault Category Decision Map',
      'Initial Investigation Priority Matrix',
      'Service History Documentation Template',
      'Electrical Assessment Summary Form',
    ],
    result:
      'A completed initial assessment that identifies the reported fault, relevant observations, and the next investigation priorities.',
    closing: 'Stop beginning every electrical investigation with scattered notes. Start with a structured assessment.',
  },
  {
    number: '02',
    title: 'HVAC Wiring Diagram Investigation Roadmap™',
    subtitle: 'Transform Complex Electrical Schematics Into an Organized Circuit Investigation',
    intro: [
      'Wiring diagrams are essential references for electrical troubleshooting. But identifying the relevant circuit and connecting it to the observed symptoms requires a consistent process.',
      'This volume provides a visual workflow for organizing circuit information and documenting the relationships between electrical components.',
    ],
    inside: [
      'Wiring Diagram Navigation Checklist',
      'Power Circuit Identification Map',
      'Control Circuit Mapping Worksheet',
      'Component Relationship Matrix',
      'Electrical Schematic Investigation Flowchart',
      'Circuit Reference Documentation Template',
    ],
    result:
      'A documented circuit map connecting equipment symptoms with the relevant electrical components and manufacturer documentation.',
    closing: 'Know which circuit you’re investigating and why it matters.',
  },
  {
    number: '03',
    title: 'HVAC Control Circuit Diagnostic Decision Trees™',
    subtitle: 'Organize Control Circuit Investigations Without Jumping Between Components',
    intro: [
      'Control circuit problems can involve multiple interacting components. Thermostats, relays, contactors, safety switches, control boards, and other devices may all influence system operation.',
      'This volume helps qualified professionals structure the investigation of these relationships.',
    ],
    inside: [
      'Thermostat Control Circuit Investigation Tree',
      'Relay & Contactor Assessment Decision Map',
      'Safety Circuit Investigation Checklist',
      'Control Board Observation Worksheet',
      'Control Signal Documentation Matrix',
      'Control Circuit Fault-Isolation Planner',
    ],
    result:
      'A structured control-circuit investigation record showing the components reviewed, findings documented, and unresolved verification requirements.',
    closing: 'Replace disconnected component checks with a logical investigation pathway.',
  },
  {
    number: '04',
    title: 'HVAC Electrical Verification & Measurement Planner™',
    subtitle: 'Organize Electrical Verification Before Recording Diagnostic Conclusions',
    intro: [
      'Electrical measurements are useful only when the testing is appropriate, authorized, and interpreted in the correct context.',
      'This volume provides a professional planning and documentation system for qualified technicians. It emphasizes manufacturer specifications, equipment-specific procedures, electrical safety prerequisites, and the limitations of individual measurements.',
    ],
    inside: [
      'Electrical Verification Planning Checklist',
      'Measurement Authorization & Safety Prerequisite Form',
      'Manufacturer Specification Comparison Worksheet',
      'Voltage Observation Recording Template',
      'Continuity & Resistance Test Documentation Form',
      'Electrical Findings Verification Matrix',
    ],
    result:
      'A structured measurement and verification record that distinguishes observed results, expected specifications, and outstanding questions.',
    closing: 'Document what was checked, under which approved conditions, and what the results actually establish.',
    safety:
      'This volume does not authorize energized testing, bypassing protective devices, or performing electrical work outside professional qualifications. Equipment isolation, lockout/tagout, verification of absence of voltage, and any permitted energized work must follow applicable requirements and approved procedures.',
  },
  {
    number: '05',
    title: 'HVAC Electrical Fault Isolation Blueprint™',
    subtitle: 'Convert Diagnostic Evidence Into a Clear Fault Investigation Summary',
    intro: [
      'A collection of measurements isn’t the same as a documented diagnostic conclusion.',
      'This volume helps technicians organize the evidence collected during an investigation and distinguish confirmed faults from possible explanations.',
    ],
    inside: [
      'Electrical Fault Isolation Decision Tree',
      'Symptom-to-Circuit Investigation Matrix',
      'Component Evidence Comparison Worksheet',
      'Fault Hypothesis Verification Checklist',
      'Confirmed vs. Unconfirmed Findings Template',
      'Recommended Next Action Planner',
    ],
    result:
      'A structured fault-isolation summary identifying supported conclusions, unresolved possibilities, and recommended next steps.',
    closing: 'Make your troubleshooting conclusions easier to review, communicate, and document.',
  },
  {
    number: '06',
    title: 'HVAC Electrical Service Documentation System™',
    subtitle: 'Turn Your Investigation Into a Clear, Professional Service Record',
    intro: [
      'The final stage of troubleshooting is more than identifying a possible problem. It involves documenting the work performed, observations recorded, findings established, and actions recommended.',
      'This volume provides a repeatable system for organizing electrical service documentation.',
    ],
    inside: [
      'Complete HVAC Electrical Troubleshooting Report',
      'Electrical Measurement Record Sheet',
      'Circuit Investigation Summary Template',
      'Fault Verification Documentation Checklist',
      'Recommended Corrective Action Form',
      'Final Service Handoff Worksheet',
    ],
    result:
      'A completed professional troubleshooting report that organizes the investigation from initial symptoms to documented findings and recommended actions.',
    closing: 'Leave every investigation with a clearer record of what was observed, verified, and recommended.',
  },
];
